/**
 * Pin a finding higher or lower in the queue with a required note (`p`).
 * Stored as a band override; it clears itself when the finding gets worse.
 */
import { useState } from "react";
import { PRIORITY_BANDS, type PriorityBand } from "@iamu/shared";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { trpc, toastTrpcError } from "@/lib/trpc";
import { toast } from "sonner";
import { PIN_LABEL, pinLabel } from "./priority-chip";

interface OverridePriorityDialogProps {
  canonicalId: string;
  projectId: string;
  subject: string;
  currentOverride: string | null;
  onClose: () => void;
  onDone?: (message: string) => void;
}

export function OverridePriorityDialog({
  canonicalId,
  projectId,
  subject,
  currentOverride,
  onClose,
  onDone,
}: OverridePriorityDialogProps) {
  const [band, setBand] = useState<string>(currentOverride ?? "P2");
  const [note, setNote] = useState("");
  const utils = trpc.useUtils();
  const mutation = trpc.finding.overridePriority.useMutation({
    onSuccess: (_, input) => {
      utils.finding.queue.invalidate({ projectId });
      utils.finding.explainPriority.invalidate({ id: canonicalId });
      const message = input.band
        ? (pinLabel(input.band) ?? "Pinned")
        : "Pin cleared";
      toast.success(message);
      onDone?.(message);
      onClose();
    },
    onError: (e) => toastTrpcError(e, "Failed to update the pin"),
  });
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Pin priority</DialogTitle>
          <DialogDescription>
            {subject}. The pin overrides the computed order and clears itself if
            a later run shows the problem getting worse.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="override-band">Pin as</Label>
            <Select value={band} onValueChange={setBand}>
              <SelectTrigger id="override-band">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PRIORITY_BANDS.map((b) => (
                  <SelectItem key={b} value={b}>
                    {PIN_LABEL[b]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="override-note">Why? (required)</Label>
            <Textarea
              id="override-note"
              rows={3}
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
          </div>
        </div>
        <DialogFooter>
          {currentOverride && (
            <Button
              variant="outline"
              disabled={mutation.isPending}
              onClick={() => mutation.mutate({ id: canonicalId, band: null })}
            >
              Clear pin
            </Button>
          )}
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            disabled={!note.trim() || mutation.isPending}
            onClick={() =>
              mutation.mutate({
                id: canonicalId,
                band: band as PriorityBand,
                note: note.trim(),
              })
            }
          >
            Pin
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
